import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { baseUrl, frontUrl } from '../config/Global';
import { getData } from '../utils/storage';
import { showToast } from '../components/Toast';
import Loader from '../components/Loader';
import PaymentRedirect from '../components/PaymentRedirect';

const Payment = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const webViewRef = useRef();
    const [loading, setLoading] = useState(false);
    const [select, setSelect] = useState(false);
    const [wait, setWait] = useState(false);
    const [data, setData] = useState(null);
    const [showWebView, setShowWebView] = useState(false);
    const [paymentInitialized, setPaymentInitialized] = useState(false);
    const [tokens, setTokens] = useState(null);
    const [paymentFormHtml, setPaymentFormHtml] = useState('');
    const [navigationHandled, setNavigationHandled] = useState(false);


    const usePaymentData = () => {
        const location = useLocation();

        const { userData, admin } = useMemo(() => {
            // 1. Try getting from state
            const stateUserData = location.state?.userData;
            const stateAdmin = location.state?.admin;

            if (stateUserData && stateAdmin) {
                return { userData: stateUserData, admin: stateAdmin };
            }

            // 2. Fallback to query string
            const params = new URLSearchParams(location.search);
            let parsedUserData = null;
            let parsedAdmin = null;

            try {
                const userDataString = params.get('userData');
                const adminString = params.get('admin');

                if (userDataString && adminString) {
                    parsedUserData = JSON.parse(decodeURIComponent(atob(userDataString)));
                    parsedAdmin = JSON.parse(decodeURIComponent(atob(adminString)));
                }
            } catch (error) {
                console.error('Failed to parse userData/admin from URL:', error);
            }

            return { userData: parsedUserData, admin: parsedAdmin };
        }, [location]);

        return { userData, admin };
    };

    const { userData, admin } = usePaymentData();

    // Get setup fee from features string
    const getSetupFee = (input) => {
        const match = input?.match(/Setup Fee\s*\$([\d.]+)/i);
        return match ? parseFloat(match[1]) : 0;
    };

    // Generate random string for signature - memoized to prevent regeneration
    const signature = useMemo(() => {
        return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    }, [tokens?.token]); // Only regenerate when token changes

    // Generate payment form HTML - memoized to prevent re-renders
    const generatePaymentForm = useMemo(() => {
        if (!tokens || !tokens.token) return '';

        const today = new Date();
        const orderDate = `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`;

        // Use the same basket ID that was used for token generation
        const basketId = tokens.basketId || '24998-SLCTZQ1TYSQ';

        console.log('Generating payment form with token:', tokens.token);
        console.log('Payment amount:', tokens.paymentAmount);
        console.log('Basket ID:', basketId);

        return `
      <html>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Payment Gateway</title>
      </head>
      <body onload="submitForm()" style="background: #f5f5f5; display: flex; justify-content: center; align-items: center; height: 100vh; margin: 0;">
        <form id="paymentForm" action="https://ipg1.apps.net.pk/Ecommerce/api/Transaction/PostTransaction" method="post">
          <input type="hidden" name="MERCHANT_ID" value="${admin?.merchant_id}">
          <input type="hidden" name="TOKEN" value="${tokens.token}">
          <input type="hidden" name="CURRENCY_CODE" value="USD">
          <input type="hidden" name="TXNAMT" value="${tokens.paymentAmount}">
          <input type="hidden" name="BASKET_ID" value="${basketId}">
          <input type="hidden" name="ORDER_DATE" value="${orderDate}">
          <input type="hidden" name="SIGNATURE" value="${signature}">
          <input type="hidden" name="VERSION" value="1.0">
          <input type="hidden" name="TXNDESC" value="Subscription Payment - ${userData?.name || 'Plan'}">
          <input type="hidden" name="PROCCODE" value="00">
          <input type="hidden" name="TRAN_TYPE" value="ECOMM_PURCHASE">
          <input type="hidden" name="CUSTOMER_MOBILE_NO" value="${tokens?.phone || '03000000000'}">
          <input type="hidden" name="CUSTOMER_EMAIL_ADDRESS" value="${tokens?.email}">
          <input type="hidden" name="SUCCESS_URL" value="${frontUrl}/Success">
          <input type="hidden" name="FAILURE_URL" value="${frontUrl}/Failed">
          <input type="hidden" name="CHECKOUT_URL" value="${frontUrl}/checkout">
          <input type="hidden" id="RECURRING_TXN" name="RECURRING_TXN" value="false" />
          <input type="hidden" name="ITEMS[0][SKU]" value="SUB-${userData?.id || '001'}" />
          <input type="hidden" name="ITEMS[0][NAME]" value="${userData?.name || 'Subscription Plan'}" />
          <input type="hidden" name="ITEMS[0][PRICE]" value="${tokens.paymentAmount}" />
          <input type="hidden" name="ITEMS[0][QTY]" value="1" />
        </form>
        <div id="loadingDiv" style="text-align: center; padding: 20px;">
          <p style="margin-bottom: 15px; font-size: 16px; color: #333;">Redirecting to payment gateway...</p>
          <p style="margin-bottom: 10px; font-size: 14px; color: #666;">Please wait while we process your request</p>
          <div style="display: inline-block; width: 40px; height: 40px; border: 4px solid #f3f3f3; border-top: 4px solid #3498db; border-radius: 50%; animation: spin 1s linear infinite;"></div>
          <style>
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          </style>
        </div>
        <script>
          function submitForm() {
            console.log('Submitting payment form...');
            setTimeout(function() {
              document.getElementById('paymentForm').submit();
            }, 2000); // Wait 2 seconds before submitting
          }
          
          // Prevent form resubmission
          let formSubmitted = false;
          document.getElementById('paymentForm').addEventListener('submit', function(e) {
            if (formSubmitted) {
              e.preventDefault();
              return false;
            }
            formSubmitted = true;
            document.getElementById('loadingDiv').innerHTML = '<p>Processing payment...</p><div style="display: inline-block; width: 40px; height: 40px; border: 4px solid #f3f3f3; border-top: 4px solid #3498db; border-radius: 50%; animation: spin 1s linear infinite;"></div>';
          });
        </script>
      </body>
      </html>
    `;
    }, [tokens, signature]);

    // Initialize payment with enhanced validation
    const initializePayment = async () => {
        if (!select) {
            showToast('Please select payment method', 'error');
            return;
        }

        if (wait) return; // Prevent multiple clicks

        setWait(true);
        try {
            let tokenData = tokens;

            // Check if token exists and is still valid (within 10 minutes)
            const tokenAge = tokens?.tokenTimestamp ? Date.now() - tokens.tokenTimestamp : 0;
            const tokenExpired = tokenAge > 10 * 60 * 1000; // 10 minutes

            if (!tokenData || !tokenData.token || tokenExpired) {
                console.log('Token missing or expired, generating new token...');
                showToast('Initializing payment...', 'info');
                tokenData = await getAccessToken(data);
            }

            // Validate token before proceeding
            if (!tokenData || !tokenData.token) {
                throw new Error('Failed to obtain valid payment token');
            }

            console.log('Proceeding with payment using token:', tokenData.token);

            // Reset navigation state
            setNavigationHandled(false);
            setPaymentInitialized(true);
            setShowWebView(true);

            showToast('Redirecting to payment gateway...', 'info');

        } catch (error) {
            console.error('Payment initialization error:', error);
            showToast('Payment initialization failed: ' + error.message, 'error');
        } finally {
            setWait(false);
        }
    };

    // Handle payment success
    const handlePaymentSuccess = async () => {
        setWait(true);
        try {
            const user = getData('data');

            const setupFee = getSetupFee(userData?.features);
            const paymentAmount = data?.mob_buy_subscription ? userData?.price : (userData?.price + (setupFee || 0));

            await axios.post(`${baseUrl}/editClient/${user.id}`, {
                plan_id: userData.id,
                plan_status: 'Active',
                mob_buy_subscription: true
            });

            await axios.post(`${baseUrl}/transaction/create`, {
                plan_id: userData.id,
                user_id: data?.id,
                status: 'Paid',
                setupFee: setupFee || 0,
                subTotal: userData?.price || 0,
                total: paymentAmount,
                payDate: new Date(),
            });

            // Clear the WebView state
            setShowWebView(false);
            setTokens(null);
            setPaymentInitialized(false);

            navigate('/paymentSuccess', {
                state: {
                    plan: userData,
                    paymentAmount: paymentAmount,
                    setupFee: setupFee
                }
            });
        } catch (error) {
            console.error('Payment verification error:', error);
            showToast('Payment verification failed', 'error');
            setShowWebView(false);
            navigate(-1);
        } finally {
            setWait(false);
        }
    };

    // Handle payment failure
    const handlePaymentFailure = () => {
        showToast('Payment failed or was cancelled', 'error');
        setShowWebView(false);
        setNavigationHandled(false);
        // navigate(-1);
        setWait(false);
    };

    // Get access token for payment with retry mechanism
    const getAccessToken = async (data, retryCount = 0) => {
        const maxRetries = 3;
        try {






            const setupFee = getSetupFee(userData?.features);
            const paymentAmount = data?.mob_buy_subscription ? userData?.price : (userData?.price + (setupFee || 0));

            // Generate unique basket ID for each request
            const uniqueBasketId = `${admin?.merchant_id}-${Date.now()}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;

            console.log('Requesting token with payload:', {
                MERCHANT_ID: admin?.merchant_id,
                BASKET_ID: uniqueBasketId,
                TXNAMT: paymentAmount?.toFixed(2),
                CURRENCY_CODE: 'USD',
            });


            // Step 2: Verify password using correct form-encoded bcrypt API call
            const form = new URLSearchParams();
            form.append('MERCHANT_ID', admin?.merchant_id);
            form.append('SECURED_KEY', admin?.secured_key);
            form.append('BASKET_ID', uniqueBasketId);
            form.append('TXNAMT', paymentAmount);
            form.append('CURRENCY_CODE', 'USD');

            const response = await axios.post(`${baseUrl}/users/getPayToken`, form, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                maxBodyLength: Infinity,
            });

            //   const response = await axios.post(
            //     `https://ipg1.apps.net.pk/Ecommerce/api/Transaction/GetAccessToken`, 
            //     {
            //       MERCHANT_ID: admin?.merchant_id,
            //       SECURED_KEY: admin?.secured_key,
            //       BASKET_ID: uniqueBasketId,
            //       TXNAMT: paymentAmount,
            //       CURRENCY_CODE: 'USD',
            //     },
            //     {
            //       timeout: 15000, // 15 second timeout
            //       headers: {
            //         'Content-Type': 'application/json',
            //         'Accept': 'application/json',
            //       }
            //     }
            //   );

            console.log('Token API Response:', response.data);

            // Validate response
            if (!response.data || !response.data.ACCESS_TOKEN) {
                throw new Error('Invalid token response: ' + JSON.stringify(response.data));
            }

            if (response.data.RESPONSE_CODE && response.data.RESPONSE_CODE !== '00') {
                throw new Error(`Token API Error: ${response.data.RESPONSE_MESSAGE || 'Unknown error'}`);
            }

            const tokenData = {
                userId: data?.id,
                email: data?.email,
                phone: data?.phone,
                paymentAmount: paymentAmount?.toFixed(2),
                addId: userData?.id,
                businessId: data?.company_id,
                token: response.data.ACCESS_TOKEN,
                basketId: uniqueBasketId,
                type: 'Subscription',
                tokenTimestamp: Date.now()
            };

            console.log('Token generated successfully:', tokenData.token);
            setTokens(tokenData);
            return tokenData;

        } catch (error) {
            console.error('Token generation error (attempt ' + (retryCount + 1) + '):', error.message);

            // Retry logic
            if (retryCount < maxRetries) {
                console.log(`Retrying token generation... (${retryCount + 1}/${maxRetries})`);
                await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds before retry
                return getAccessToken(data, retryCount + 1);
            }

            showToast('Failed to initialize payment after multiple attempts', 'error');
            throw error;
        }
    };

    // Handle WebView navigation
    const handleWebNavigation = () => {
        const url = window.location.href;

        if (!url || navigationHandled) return;


        // Handle successful payment
        if (url.includes(`${frontUrl}/Success`) ||
            url.includes('/Success')) {
            console.log('Payment Success detected');
            setNavigationHandled(true);
            handlePaymentSuccess();
            return false;
        }

        // Handle failed payment
        if (url.includes(`${frontUrl}/Failed`) ||
            url.includes('/Failed')) {
            console.log('Payment Failure detected');
            setNavigationHandled(true);
            handlePaymentFailure();
            return false;
        }

        // Handle payment gateway errors
        // if (url.includes('error')) {
        //     console.log('Payment Gateway Error detected');
        //     setNavigationHandled(true);
        //     handlePaymentFailure();
        //     return false;
        // }

        // Log all navigation for debugging
        if (url.includes('ipg1.apps.net.pk')) {
            console.log('Payment gateway navigation:', url);
        }
    };


    useEffect(() => {
        handleWebNavigation();
    }, [location]);



    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const user = getData('data');
                const response = await axios.get(`${baseUrl}/users/get/${user?.id}`);
                setData(response?.data?.data);


                const tokenAge = tokens?.tokenTimestamp ? Date.now() - tokens?.tokenTimestamp : 0;
                const tokenExpired = tokenAge > 10 * 60 * 1000; // 10 minutes


                if (!tokens || tokenExpired || !admin) {
                    console.log('Fetching fresh token...');
                    setWait(true);

                    await getAccessToken(response?.data?.data);
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
                showToast('Failed to load user data', 'error');
            } finally {
                setWait(false);
            }
        };

        fetchUserData();
        // handleWebNavigation()
    }, []);

    // WebView component for payment
    if (showWebView && tokens && generatePaymentForm) {


        return (
            <div className="relative min-h-screen bg-gray-50">
                {/* Header */}
                <div className="bg-white shadow-sm">
                    <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <div className="w-28 h-10  rounded-lg flex items-center justify-center">
                                    <img src={require('../assets/logo.png')} />
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    setShowWebView(false);
                                    navigate(-1);
                                }}
                                className="flex items-center space-x-1 text-gray-600 hover:text-gray-900"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                <span className="font-medium">Back</span>
                            </button>

                        </div>
                    </div>
                </div>

                {/* WebView Container */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="bg-white rounded-xl shadow-lg overflow-hidden" style={{ height: 'calc(100vh - 180px)' }}>

                        <PaymentRedirect tokens={tokens} admin={admin} signature={signature} userData={userData} />
                    </div>
                </div>

                {/* Loading overlay */}
                {wait && (
                    <div className="fixed inset-0 bg-gray bg-opacity-30 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 max-w-sm w-full shadow-xl">
                            <div className="flex flex-col items-center">
                                <div className="w-12 h-12 border-4 border-gray-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                                <p className="text-gray-700 font-medium">Processing payment...</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="bg-gradient-to-br from-gray-50 via-indigo-50 to-lime-50 min-h-screen">
            {wait && <Loader />}

            {/* Background decoration */}
            <div className="absolute inset-0">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-lime-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse-slow"></div>
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gray-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse-slow"></div>
            </div>

            <div className="relative">
                {/* Header */}
                <div className="bg-white/80 backdrop-blur-lg shadow-lg border-b border-white/20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex items-center justify-between h-16">

                            <div className="flex items-center">
                                <div className="w-28 h-10  rounded-lg flex items-center justify-center">
                                    <img src={require('../assets/logo.png')} />
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    // Clear local storage
                                    localStorage.clear();
                                    // Optionally clear session storage if used
                                    sessionStorage.clear();
                                    // Redirect to login page or home page
                                    navigate('/'); // Change this to your desired logout destination
                                }}
                                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                                    />
                                </svg>
                                <span className="font-medium">Logout</span>
                            </button>

                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl p-6 border border-white/20 mb-8">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">Select Payment Method</h2>

                        <div className="bg-gray-50 p-6 rounded-xl mb-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                    <div
                                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center cursor-pointer transition-colors ${select ? 'border-gray-500 bg-gray-500' : 'border-gray-400'}`}
                                        onClick={() => setSelect(!select)}
                                    >
                                        {select && (
                                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </div>
                                    <span className="font-medium text-gray-800">Pay with Payfast</span>
                                </div>
                                <img src={require('../assets/pf.png')} alt="Payfast" className="h-8" />
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                            <h3 className="text-xl font-bold text-gray-800 mb-4">Package Details</h3>
                            <div className="space-y-3">
                                <h4 className="text-lg font-semibold text-gray-600">{userData?.name}</h4>
                                <ul className="space-y-2">
                                    <li className="flex items-start">
                                        <span className="text-gray-600 mr-2">1.</span>
                                        <span>Contact limit ({userData?.limit_items})</span>
                                    </li>
                                    {userData?.features?.split(',').map((feature, index) => (
                                        <li key={index} className="flex items-start">
                                            <span className="text-gray-600 mr-2">{index + 2}.</span>
                                            <span>{feature.trim()}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        {!data?.mob_buy_subscription && (
                            <>
                                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                                    <span className="text-gray-600">First Time Setup Fee:</span>
                                    <span className="font-medium">${(tokens?.paymentAmount - userData?.price)?.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                                    <span className="text-gray-600">Package amount:</span>
                                    <span className="font-medium">${userData?.price?.toFixed(2)}</span>
                                </div>
                            </>
                        )}

                        <div className="flex justify-between items-center py-4 mt-4 bg-gray-50 rounded-lg px-4">
                            <span className="text-lg font-bold text-gray-800">Total amount:</span>
                            <span className="text-xl font-bold text-gray-600">${tokens?.paymentAmount || userData?.price?.toFixed(2)}</span>
                        </div>
                    </div>

                    <button
                        onClick={initializePayment}
                        disabled={wait || !tokens || !select}
                        className={`w-full py-3 px-6 rounded-xl font-semibold text-white shadow-lg transition-all duration-200 ${wait || !tokens || !select
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-[#a8e82f] hover:shadow-xl hover:-translate-y-0.5 text-black'
                            }`}
                    >
                        {wait ? (
                            <div className="flex items-center justify-center space-x-2">
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                <span>Processing...</span>
                            </div>
                        ) : (
                            'Confirm & Pay'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Payment;